// src/pages/Health.jsx
// the Health page (3.41.0, Super Admins): is the fax service working?
//   - every check now (backend lib/systemStatus.js), green / amber / red
//   - the test fax between our two test lines (Crazy Pixels → Shanthi):
//     each weekday at the time set here, and Run test now; each step timed
//   - alert emails to admin@ (one when a check goes wrong, one when it's
//     back), with their history, and the switches
//   - 3.62.4 (owner, 2026-10-15): WhatsApp alerts sent to Super Admins (every
//     message, above the test fax), and every list sorts by date (newest or
//     oldest first), filters, and pages 25 at a time (Show 25/50/100)
//   - 3.62.5: picture WhatsApp messages: the banner shows in the list too
//   - 3.62.7 (owner): compact rows: a small banner first, the message on
//     two lines at most (click for all of it)
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import { formatDate } from '../utils/format';
import { SECTION_SUB } from '../theme/tabs';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import StatusIcon, { statusGlow } from '../components/StatusIcon';
import Pager, { usePager } from '../components/Pager';

const COLOR = { green: 'var(--t-2e7d32)', amber: 'var(--t-ef6c00)', red: 'var(--t-c62828)' };
const BG = { green: 'var(--c-f1f8e9)', amber: 'var(--c-fff8e1)', red: 'var(--c-ffebee)' };
const DOT = { green: '●', amber: '●', red: '●' };
const RESULT = { passed: ['✓ Passed', 'var(--t-2e7d32)', 'var(--c-e8f5e9)'], failed: ['✕ Failed', 'var(--t-c62828)', 'var(--c-ffebee)'], running: ['… Running', 'var(--t-1565c0)', 'var(--c-e3f2fd)'] };

const phone = n => { const d = String(n || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n; };
const secs = s => (s == null ? '—' : s < 120 ? `${s} s` : `${Math.round(s / 60)} min`);
const gap = (a, b) => (a && b ? secs(Math.max(0, Math.round((Date.parse(b) - Date.parse(a)) / 1000))) : '—');
const to12 = hm => { const [h, m] = hm.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };

const NONE = [];
const WA_KIND = { health: 'Health alert', 'fax by email': 'Fax by email', balance: 'Low balance', test: 'Test', other: 'Other' };
// "+91 98765 43210" → "+91 …3210" (the full number is in My Profile)
const maskWa = n => { const d = String(n || '').replace(/\D/g, ''); return d.length > 4 ? `+${d.slice(0, d.length > 10 ? d.length - 10 : 1)} …${d.slice(-4)}` : n; };

// A list's sort (newest / oldest first), filter and page size; the rows
// sorted and filtered for usePager
function useListView(rows, dateKey, filterFn) {
  const [order, setOrder] = useState('desc');
  const [filter, setFilter] = useState('all');
  const [show, setShow] = useState(25);
  const shown = useMemo(() => {
    const list = (rows || NONE).filter(r => filter === 'all' || filterFn(r, filter));
    return [...list].sort((a, b) => (order === 'desc' ? -1 : 1) * String(a[dateKey] || '').localeCompare(String(b[dateKey] || '')));
  }, [rows, dateKey, filterFn, filter, order]);
  const { pageRows, pager } = usePager(shown, show);
  return { order, setOrder, filter, setFilter, show, setShow, pageRows, pager, count: shown.length };
}
function ListBar({ v, filters, label }) {
  return (
    <div style={S.listBar}>
      <label style={S.barLabel}>Sort
        <select value={v.order} onChange={e => v.setOrder(e.target.value)} style={S.select} aria-label={`Sort ${label}`}>
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </select>
      </label>
      <label style={S.barLabel}>Show
        <select value={v.filter} onChange={e => v.setFilter(e.target.value)} style={S.select} aria-label={`Filter ${label}`}>
          {filters.map(([val, text]) => <option key={val} value={val}>{text}</option>)}
        </select>
      </label>
      <label style={S.barLabel}>Per page
        <select value={v.show} onChange={e => v.setShow(Number(e.target.value))} style={S.select} aria-label={`${label} per page`}>
          {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
    </div>
  );
}
// 3.62.7 (owner): the message on one line (its lines joined), two lines at
// most; click to see it all, so 25 messages fit without much scrolling
function WaText({ text }) {
  const [open, setOpen] = useState(false);
  const lines = String(text || '').split('\n').map(x => x.trim()).filter(Boolean);
  const short = lines.join(' · ');
  return open
    ? <button type="button" onClick={() => setOpen(false)} style={{ ...S.waText, whiteSpace: 'pre-line' }} title="Show less">{lines.join('\n')}</button>
    : <button type="button" onClick={() => setOpen(true)} style={{ ...S.waText, ...S.clamp }} title="Show the whole message">{short}</button>;
}
const testFilter = (t, f) => t.status === f;
const alertFilter = (a, f) => (f === 'resolved' ? a.kind === 'resolved' : a.kind !== 'resolved');
const waFilter = (w, f) => (f === 'failed' ? !w.ok : f === 'sent' ? w.ok : w.kind === f);
// 3.67.6: the support site's email-ticket runs that needed a retry or failed
const stFilter = (r, f) => (f === 'failed' ? !r.ok : r.ok);

export default function Health() {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const timer = useRef(null);

  const call = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API_BASE}/portal/control/health${path}`, { ...opts, headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json', ...(opts.headers || {}) } });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'Something went wrong.');
    return j;
  }, [getToken]);
  const load = useCallback(async (fresh = false) => {
    try { setData(await call(fresh ? '?fresh=1' : '')); setError(''); } catch (e) { setError(e.message); }
  }, [call]);
  useEffect(() => { load(); }, [load]);
  // While a test runs, look again every 20 seconds
  const running = data?.tests?.some(t => t.status === 'running');
  useEffect(() => {
    clearInterval(timer.current);
    if (running) timer.current = setInterval(() => load(), 20000);
    return () => clearInterval(timer.current);
  }, [running, load]);
  // 3.49.20 (owner): 25 a page; 3.62.4: sort, filter, Show 25/50/100
  const tests = useListView(data?.tests, 'startedAt', testFilter);
  const alerts = useListView(data?.alerts, 'at', alertFilter);
  const wa = useListView(data?.whatsapp, 'at', waFilter);
  const stRuns = useListView(data?.supportTickets?.problems, 'at', stFilter);

  async function runTest() {
    const ok = await confirm({ title: 'Send a test fax now?', message: `A one-page test fax goes from the Crazy Pixels line ${phone(data.lines.from.number)} to the Shanthi line ${phone(data.lines.to.number)}. It costs a few cents on Telnyx and takes a minute or two.`, confirmLabel: 'Send test fax' });
    if (!ok) return;
    setBusy('test');
    try { await call('/test', { method: 'POST' }); notify({ bell: 'fax', tone: 'success', title: 'Test fax started', detail: 'The result shows here in a minute or two.' }); await load(); }
    catch (e) { notify({ tone: 'error', title: "Couldn't start the test", detail: e.message }); }
    setBusy('');
  }
  async function save(patch) {
    setBusy('save');
    try { const j = await call('/settings', { method: 'PUT', body: JSON.stringify(patch) }); setData(d => ({ ...d, settings: j.settings })); notify({ tone: 'success', title: 'Saved' }); }
    catch (e) { notify({ tone: 'error', title: "Couldn't save", detail: e.message }); }
    setBusy('');
  }

  if (!data) return error ? <div style={S.error}>{error}</div> : <Loader label="Checking everything…" />;
  const st = data.status || {};
  const checks = st.checks || [];
  const overall = st.overall || st.level;
  const cfg = data.settings || {};
  const lastTest = data.tests?.[0];
  return (
    <div>
      <div style={S.head}>
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <p style={SECTION_SUB}>Is the fax service working? Every check below runs every few minutes. A test fax between our own two test lines proves faxes really go through, and admin@ gets an email when something goes wrong.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flex: '0 0 auto', marginLeft: 'auto' }}>
          <button onClick={() => load(true)} style={S.refresh}><Emo e="↻" /> Check now</button>
          <button onClick={runTest} disabled={busy === 'test' || running} style={{ ...S.primary, opacity: busy === 'test' || running ? 0.6 : 1 }}>
            {running ? 'Test running…' : busy === 'test' ? 'Starting…' : <><Emo e="📠" /> Run test now</>}
          </button>
        </div>
      </div>
      {error && <div style={S.error}>{error}</div>}

      <div style={{ ...S.banner, background: BG[overall], borderColor: COLOR[overall] }} className={statusGlow(overall) || undefined}>
        <StatusIcon level={overall} size={28} />{/* 3.68.9 (owner): was a dot pulsing when not green */}
        <div>
          <div style={{ fontWeight: 800, color: COLOR[overall], fontSize: 16 }}>
            {overall === 'green' ? 'Everything is working' : overall === 'amber' ? 'Something needs a look' : 'Something is wrong'}
          </div>
          <div style={S.muted}>What clients see on their Overview: “{st.message}”. Checked {formatDate(st.checkedAt)}.</div>
        </div>
      </div>

      <div style={S.grid}>
        {checks.map((c, i) => (
          <div key={c.id} className="ov-lift" style={{ ...S.check, borderLeft: `4px solid ${COLOR[c.level]}`, animationDelay: `${i * 40}ms` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: COLOR[c.level], fontSize: 12 }} aria-hidden="true">{DOT[c.level]}</span>
              <strong style={{ fontSize: 14, color: 'var(--t-263238)' }}>{c.label}</strong>
              {c.public && <span style={S.pub} title="Part of the status clients see on their Overview">Overview</span>}
              {data.open?.[c.id] && <span style={S.since} title="Open since">since {formatDate(data.open[c.id].since)}</span>}
            </div>
            <div style={S.detail}>{c.detail}</div>
          </div>
        ))}
      </div>

      {/* 3.67.6 (owner): how often the support site needed a retry or didn't answer */}
      <section style={S.section}>
        <h4 style={S.h4}><Emo e="🛟" /> Support email tickets</h4>
        <p style={S.muted}>
          Every 5 minutes our backend asks support.crazypixels.org to turn new emails to support@ into tickets (Cloud Scheduler job “support-email-tickets”, in place of the old Apps Script trigger).
          If the site doesn’t answer, it tries again after 10, 20 and 40 seconds. Only the checks that needed a retry or didn’t get through are listed here (90 days).
          {data.supportTickets?.week && <> Last 7 days: <strong>{data.supportTickets.week.runs.toLocaleString()}</strong> checks, <strong>{data.supportTickets.week.retried}</strong> got through on a retry, <strong>{data.supportTickets.week.failed}</strong> didn’t get through.</>}
          {data.supportTickets?.month && <> Last 30 days: {data.supportTickets.month.runs.toLocaleString()} checks, {data.supportTickets.month.retried} on a retry, {data.supportTickets.month.failed} didn’t get through.</>}
        </p>
        <ListBar v={stRuns} label="support email ticket checks" filters={[['all', 'All listed'], ['retried', 'Got through on a retry'], ['failed', 'Didn’t get through']]} />
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>{['When', 'Result', 'Tries', 'What went wrong'].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {!stRuns.count && <tr><td colSpan={4} style={S.empty}>{data.supportTickets?.problems?.length ? 'None match.' : 'None so far: every check got through on the first try.'}</td></tr>}
              {stRuns.pageRows.map(r => (
                <tr key={r.at}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatDate(r.at)}</td>
                  <td style={S.td}>{r.ok
                    ? <span style={{ ...S.pill, color: 'var(--t-e65100)', background: 'var(--c-fff3e0)' }}>Got through on a retry</span>
                    : <span style={{ ...S.pill, color: 'var(--t-c62828)', background: 'var(--c-ffebee)' }}>✕ Didn’t get through</span>}</td>
                  <td style={S.td}>{r.tries}</td>
                  <td style={{ ...S.td, color: 'var(--t-546e7a)' }}>{[...new Set(r.problems || [])].join(' · ') || r.error || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager {...stRuns.pager} label="support email ticket checks" />
      </section>

      <section style={S.section}>
        <h4 style={S.h4}><Emo e="💬" /> WhatsApp alerts</h4>
        <p style={S.muted}>
          Critical problems go by WhatsApp to every Super Admin with a WhatsApp number in My Profile{data.whatsappTo?.length ? ` (now: ${data.whatsappTo.map(maskWa).join(', ')})` : ' (nobody has one yet)'}:
          every red check above, File storage and “Fax by email: emails waiting” when amber, and an email fax by email had to give up on, each with a “back to normal” message. Low balances go by WhatsApp too. Each message has a banner (🔴 Critical when faxing is affected, 🟠 Needs a look otherwise), the part affected, what it means and what to check first.
          {!cfg.alertsOn && ' The Alert emails switch below is off, so Health alerts aren\'t sent now.'} “Sent” means WhatsApp accepted the message.
        </p>
        <ListBar v={wa} label="WhatsApp messages" filters={[['all', 'All messages'], ['sent', 'Sent'], ['failed', 'Not sent'], ['health', 'Health alerts'], ['fax by email', 'Fax by email'], ['balance', 'Low balance'], ['test', 'Tests']]} />
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>{['', 'Sent', 'To', 'Why', 'Message', 'Result'].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {!wa.count && <tr><td colSpan={6} style={S.empty}>{data.whatsapp?.length ? 'No messages match.' : 'No WhatsApp messages yet. They are listed here from 3.62.4 on.'}</td></tr>}
              {wa.pageRows.map(w => (
                <tr key={w.id}>
                  <td style={{ ...S.td, width: 74, paddingRight: 0 }}>{w.image ? <img src={w.image} alt="" style={{ display: 'block', width: 72, borderRadius: 5 }} /> : <span style={S.noPic}><Emo e="💬" /></span>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatDate(w.at)}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{maskWa(w.to)}</td>
                  <td style={S.td}>{WA_KIND[w.kind] || w.kind}</td>
                  <td style={{ ...S.td, color: 'var(--t-546e7a)', maxWidth: 520 }}><WaText text={w.text} /></td>
                  <td style={S.td}>{w.ok
                    ? <span style={{ ...S.pill, color: 'var(--t-2e7d32)', background: 'var(--c-e8f5e9)' }}>✓ Sent</span>
                    : <span style={{ ...S.pill, color: 'var(--t-c62828)', background: 'var(--c-ffebee)' }} title={w.error || ''}>✕ Not sent</span>}
                    {!w.ok && w.error && <div style={{ fontSize: 11.5, color: 'var(--t-c62828)', marginTop: 3, maxWidth: 260 }}>{w.error}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager {...wa.pager} label="WhatsApp messages" />
      </section>

      <section style={S.section}>
        <h4 style={S.h4}><Emo e="📠" /> Test fax between our test lines</h4>
        <p style={S.muted}>
          From the Crazy Pixels line {phone(data.lines.from.number)} (as {data.lines.from.email}) to the Shanthi line {phone(data.lines.to.number)}, through the normal queue.
          It fails if it isn't sent within {data.limits.waitMin} minutes, or not delivered and received within {data.limits.resultMin} minutes of sending.
          It sends no emails or pop-ups, isn't retried or billed, and isn't counted on the Overview or in Contacts; it's listed in those two clients' Outbox and Inbox as “Health check”.
        </p>
        <div style={S.settings}>
          <label style={S.switchRow}>
            <input type="checkbox" checked={!!cfg.testsOn} disabled={busy === 'save'} onChange={e => save({ testsOn: e.target.checked })} />
            <span>Send a test fax each weekday at</span>
          </label>
          <select value={cfg.testTime} disabled={busy === 'save' || !cfg.testsOn} onChange={e => save({ testTime: e.target.value })} style={S.select} aria-label="Test time (Eastern)">
            {Array.from({ length: 24 * 2 }, (_, k) => `${String(Math.floor(k / 2)).padStart(2, '0')}:${k % 2 ? '30' : '00'}`).filter(t => t >= '06:00' && t <= '20:00').map(t => <option key={t} value={t}>{to12(t)} ET</option>)}
          </select>
          {lastTest && <span style={S.muted}>Last test: {RESULT[lastTest.status]?.[0] || lastTest.status}, {formatDate(lastTest.startedAt)}</span>}
        </div>
        <ListBar v={tests} label="test faxes" filters={[['all', 'All tests'], ['passed', 'Passed'], ['failed', 'Failed'], ['running', 'Running']]} />
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>{['Started', 'Why', 'Result', 'Waited', 'Sent → delivered', 'Sent → received', 'Total', 'Note'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {!tests.count && <tr><td colSpan={8} style={S.empty}>{data.tests.length ? 'No tests match.' : 'No test yet. Press Run test now, or wait for the next weekday morning.'}</td></tr>}
              {tests.pageRows.map(t => {
                const s = t.steps || {}; const [label, color, bg] = RESULT[t.status] || [t.status, 'var(--t-546e7a)', 'var(--c-eceff1)'];
                return (
                  <tr key={t.id}>
                    <td style={S.td}>{formatDate(t.startedAt)}</td>
                    <td style={S.td}>{t.trigger === 'schedule' ? 'Weekday test' : `Run by ${String(t.by || '').split('@')[0]}`}</td>
                    <td style={S.td}><span style={{ ...S.pill, color, background: bg }}>{label}</span></td>
                    <td style={S.td}>{s.sentAt ? gap(t.startedAt, s.sentAt) : '—'}</td>
                    <td style={S.td}>{gap(s.sentAt, s.deliveredAt)}</td>
                    <td style={S.td}>{gap(s.sentAt, s.receivedAt)}</td>
                    <td style={S.td}><strong>{secs(t.seconds)}</strong></td>
                    <td style={{ ...S.td, color: t.status === 'failed' ? 'var(--t-c62828)' : 'var(--t-78909c)', maxWidth: 340 }}>{t.reason || (t.status === 'running' ? (s.sentAt ? 'Sent, waiting for the result…' : 'Waiting for the line…') : '')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager {...tests.pager} label="test faxes" />
      </section>

      <section style={S.section}>
        <h4 style={S.h4}><Emo e="✉️" /> Alert emails</h4>
        <label style={S.switchRow}>
          <input type="checkbox" checked={!!cfg.alertsOn} disabled={busy === 'save'} onChange={e => save({ alertsOn: e.target.checked })} />
          <span>Email {data.alertTo}{data.alertCc ? ` (copy to ${data.alertCc})` : ''} when a check stays wrong for {data.graceMin} minutes (a failed test fax: straight away), and again when it's back to normal</span>
        </label>
        <ListBar v={alerts} label="alert emails" filters={[['all', 'All alerts'], ['problem', 'Went wrong'], ['resolved', 'Back to normal']]} />
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>{['When', 'Check', 'What happened', 'Detail', 'Email'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {!alerts.count && <tr><td colSpan={5} style={S.empty}>{data.alerts.length ? 'No alerts match.' : <>No alerts yet. <Emo e="🎉" /></>}</td></tr>}
              {alerts.pageRows.map(a => (
                <tr key={a.id}>
                  <td style={S.td}>{formatDate(a.at)}</td>
                  <td style={S.td}>{a.label}</td>
                  <td style={S.td}><span style={{ ...S.pill, ...(a.kind === 'resolved' ? { color: 'var(--t-2e7d32)', background: 'var(--c-e8f5e9)' } : { color: COLOR[a.level] || 'var(--t-c62828)', background: BG[a.level] || 'var(--c-ffebee)' }) }}>{a.kind === 'resolved' ? '✓ Back to normal' : <><Emo e="⚠️" /> Went wrong</>}</span></td>
                  <td style={{ ...S.td, color: 'var(--t-546e7a)', maxWidth: 420 }}>{a.detail}</td>
                  <td style={S.td}>{a.emailed ? 'Sent' : 'Off'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager {...alerts.pager} label="alert emails" />
      </section>
    </div>
  );
}

const S = {
  head: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13, margin: '10px 0' },
  refresh: { padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  primary: { padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  banner: { display: 'flex', alignItems: 'center', gap: 14, margin: '14px 0', padding: '14px 18px', borderRadius: 14, border: '1.5px solid' },
  muted: { color: 'var(--t-78909c)', fontSize: 13, lineHeight: 1.5, margin: '2px 0 0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 },
  check: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.04)' },
  detail: { fontSize: 12.5, color: 'var(--t-546e7a)', marginTop: 5, lineHeight: 1.45 },
  pub: { fontSize: 10, fontWeight: 800, color: 'var(--t-00695c)', background: 'var(--c-e0f2f1)', padding: '1px 7px', borderRadius: 8, textTransform: 'uppercase', letterSpacing: '0.4px' },
  since: { fontSize: 11, color: 'var(--t-c62828)', marginLeft: 'auto' },
  section: { marginTop: 26, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 14, padding: '16px 18px' },
  h4: { margin: '0 0 6px', color: 'var(--t-00695c)', fontSize: 16 },
  settings: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '12px 0' },
  switchRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--t-37474f)', cursor: 'pointer', margin: '10px 0' },
  select: { padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit' },
  tableWrap: { overflowX: 'auto', marginTop: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', background: 'var(--c-00695c)', color: 'var(--t-ffffff)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid var(--c-eceff1)', verticalAlign: 'top' },
  empty: { padding: '18px 10px', color: 'var(--t-90a4ae)', textAlign: 'center' },
  waText: { all: 'unset', cursor: 'pointer', display: 'block', fontSize: 13, lineHeight: 1.45, color: 'var(--t-546e7a)' },
  clamp: { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  noPic: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 38, borderRadius: 5, background: 'var(--c-eceff1)', fontSize: 18 },
  listBar: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', margin: '10px 0 2px' },
  barLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--t-546e7a)', fontWeight: 600 },
  pill: { display: 'inline-block', padding: '2px 9px', borderRadius: 10, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
};
